/**
 * Account API — client & internal account reads + CRUD.
 * Uses the shared `api` instance; returns the unwrapped `data` payload.
 * Holders of ACCOUNT_MANAGE create / edit accounts, toggle ACTIVE/INACTIVE, and
 * can permanently delete an account along with all of its data (projects, change
 * requests, milestones, RAID items, status reports). Every mutation invalidates
 * the ['accounts'] list key; update (weekend change) and delete also refresh the
 * portfolio schedule.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import { SCHEDULE_KEYS } from './schedule';
import { invalidateDashboards } from './dashboard';
import type { AccountResponse, AccountStatus, WeekendConvention } from '@/types/domain';

interface ApiEnvelope<T> {
  data: T;
  error: { code: string; message: string } | null;
  meta?: Record<string, unknown>;
}

/** Create body — name + key + type required; key is an immutable lowercase slug. */
export interface AccountCreatePayload {
  name: string;
  key: string;
  type: string;
  leadMemberId?: string | null;
  weekendConvention?: WeekendConvention;
  notes?: string | null;
}

/** Update body — name + type required; key is NOT updatable (immutable). */
export interface AccountUpdatePayload {
  name: string;
  type: string;
  leadMemberId?: string | null;
  weekendConvention?: WeekendConvention;
  notes?: string | null;
}

/** GET /account — all accounts (unpaged list). */
export async function fetchAccounts(): Promise<AccountResponse[]> {
  const res = await api.get<ApiEnvelope<AccountResponse[]>>('/account');
  return res.data.data;
}

export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: fetchAccounts,
    staleTime: 60_000,
  });
}

export function useCreateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AccountCreatePayload) =>
      api.post<ApiEnvelope<AccountResponse>>('/account', payload).then((res) => res.data.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      invalidateDashboards(qc);
    },
  });
}

export function useUpdateAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: AccountUpdatePayload }) =>
      api.put<ApiEnvelope<AccountResponse>>(`/account/${id}`, payload).then((res) => res.data.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      // A weekend-convention change recomputes the schedule server-side → refresh it.
      void qc.invalidateQueries({ queryKey: SCHEDULE_KEYS.portfolio });
      invalidateDashboards(qc);
    },
  });
}

export function useSetAccountStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: AccountStatus }) =>
      api
        .patch<ApiEnvelope<AccountResponse>>(`/account/${id}/status`, { status })
        .then((res) => res.data.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      invalidateDashboards(qc);
    },
  });
}

/** Permanently delete an account and every record tied to it (irreversible cascade). */
export function useDeleteAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<ApiEnvelope<void>>(`/account/${id}`).then((res) => res.data.data),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['accounts'] });
      void qc.invalidateQueries({ queryKey: SCHEDULE_KEYS.portfolio });
      invalidateDashboards(qc);
    },
  });
}
